import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { extractError } from "@/api/client";
import { Delete } from "lucide-react";

const LOGO_URL = "/kilolava-logo.png";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "back"];

export default function LoginPage() {
  const { user, loginWithPin } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = useCallback(async (fullPin) => {
    setLoading(true);
    setError("");
    try {
      const u = await loginWithPin(fullPin);
      const welcome = { it: "Bentornato", en: "Welcome back", es: "Bienvenida", ur: "خوش آمدید" };
      const lang = u.preferred_language || "it";
      toast.success(`${welcome[lang] || welcome.it}, ${u.full_name}!`);
      navigate("/");
    } catch (err) {
      setError(extractError(err));
      setPin("");
    } finally {
      setLoading(false);
    }
  }, [loginWithPin, navigate]);

  // Auto-submit as soon as 4 digits are entered
  useEffect(() => {
    if (pin.length === 4 && !loading) {
      submit(pin);
    }
  }, [pin, loading, submit]);

  const press = (k) => {
    if (loading) return;
    setError("");
    if (k === "back") {
      setPin((p) => p.slice(0, -1));
    } else if (/^[0-9]$/.test(k) && pin.length < 4) {
      setPin((p) => p + k);
    }
  };

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => {
      if (loading) return;
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") press("back");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, loading]);

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3">
          <img src={LOGO_URL} alt="Kilolava" className="h-16 w-auto" data-testid="login-logo" />
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter leading-none text-[#1F1F1F]">
              Ciao.
            </h1>
            <p className="mt-1 text-gray-500 text-sm">Inserisci il tuo PIN per accedere.</p>
          </div>
        </div>

        {/* PIN dots */}
        <div className="flex items-center justify-center gap-4 mb-6" data-testid="pin-dots">
          {[0, 1, 2, 3].map((i) => {
            const filled = i < pin.length;
            const isError = !!error;
            return (
              <div
                key={i}
                data-testid={`pin-dot-${i}`}
                className={`w-4 h-4 rounded-full transition-all duration-150 ${
                  isError
                    ? "bg-red-500 scale-110"
                    : filled
                    ? "bg-[#EC4E5B] scale-110"
                    : "bg-gray-300"
                }`}
              />
            );
          })}
        </div>

        {error && (
          <div
            data-testid="login-pin-error"
            className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-center"
          >
            {error}
          </div>
        )}

        {/* Numeric keypad */}
        <div className="grid grid-cols-3 gap-3" data-testid="pin-keypad">
          {KEYS.map((k, idx) => {
            if (k === "") return <div key={idx} />;
            if (k === "back") {
              return (
                <Button
                  key={idx}
                  variant="ghost"
                  onClick={() => press("back")}
                  disabled={loading || pin.length === 0}
                  data-testid="pin-key-back"
                  className="h-16 rounded-2xl text-gray-500 hover:bg-gray-100 active:scale-95 transition-transform disabled:opacity-30"
                >
                  <Delete className="w-6 h-6" />
                </Button>
              );
            }
            return (
              <Button
                key={idx}
                variant="ghost"
                onClick={() => press(k)}
                disabled={loading}
                data-testid={`pin-key-${k}`}
                className="h-16 rounded-2xl text-2xl font-semibold text-[#1F1F1F] hover:bg-gray-100 active:scale-95 transition-transform border border-gray-200 bg-white"
              >
                {k}
              </Button>
            );
          })}
        </div>

        {loading && (
          <p className="mt-6 text-center text-sm text-gray-500" data-testid="pin-loading">
            Verifica PIN…
          </p>
        )}

        <p className="mt-8 text-xs text-gray-400 text-center">
          Kilolava Operations · gestionale pulizie professionali
        </p>
      </div>
    </div>
  );
}
